import { Alert } from '@mm/components';
import { useSafeTimeout } from '@mm/hooks';
import { usePageCRUDActions } from '@mm/state/actions/PageCRUDActions';
import ButtonModel, {
  ButtonType,
  PageButtonConfig,
  WebviewOptions,
} from '@mm/state/model/ButtonModel';
import { PageKind } from '@mm/state/model/PageModel';
import WidgetModel from '@mm/state/model/WidgetModel';
import { pageEntityByIdSelector } from '@mm/state/selectors/pageSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classnames from 'classnames';
import { useFormik } from 'formik';
import { isFunction } from 'lodash';
import moment from 'moment';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { getUpdatedTextWithAttribute } from '@mm/state/helpers/attribute-picker-helpers';
import AttributePicker from '../../../../../../common/components/AttributePicker/AttributePicker';
import { getUpdatedTextWithEmoji } from '../../../../../../common/components/EmojiPicker/emoji-picker-helpers';
import { EmojiPicker } from '../../../../../../common/components/EmojiPicker/EmojiPicker';
import { generatePageStructure } from '@mm/state/helpers/filterable-select-helpers';
import FormFieldWrapper from '../../../../../../common/components/FormFieldWrapper/FormFieldWrapper';
import { PhoneInput } from '../../../../../../common/components/PhoneInput/PhoneInput';
import PrimarySelect from '../../../../../../common/components/PrimarySelect/PrimarySelect';
import TextInput from '../../../../../../common/components/TextInput';
import { TooltipContainer } from '../../../../../../common/components/TooltipContainer/TooltipContainer';
import { WrappedTextInput } from '../../../../../../common/components/WrappedTextInput/WrappedTextInput';
import { PathGraphContext } from '../../../../../../common/widgets/common/PathGraph/PathGraph';
import { useInputRef } from '../../../../../../common/widgets/common/use-input-ref';
import {
  TextWidgetButtonDefinition,
  textWidgetButtons,
} from '../../../../../../common/widgets/TextWidget/CreateEditButtonArea/buttons-list';
import FormHelpers, {
  CreateEditButtonFormValues,
} from '../../../../../../common/widgets/TextWidget/CreateEditButtonArea/create-edit-button-form-helpers';
import AttributeContainingTextInput from '../../../../../components/AttributeContainingTextInput/AttributeContainingTextInput';
import { MAX_BUTTON_LENGTH } from '../../../../configure/components/InputBase';
import { WidgetPlatforms } from '../../../../startBotPromoters/common/StartBotsDialogueEditor/WidgetByType/WidgetPlatforms/WidgetPlatforms';
import { ButtonTagsForm } from '../buttons/ButtonConfigform/ButtonTagsForm';
import { MAP_ENTITY } from '../buttons/constants';
import './EditButtonArea.pcss';

type Props = {
  widget: WidgetModel;
  button: ButtonModel | Partial<ButtonModel>;
  buttonIndex: number;
  onCreateButtonHandler: (button: ButtonModel) => void;
  onUpdateButtonHandler: (buttonIndex: number, button: ButtonModel) => void;
  closeButtonArea: () => any;
  setUnsavedEntryButton?: (unsavedButton: ButtonModel | undefined) => void;
  disabledButtonTypes?: ButtonType[];
  usedInSlideout?: boolean;
};

export const useDialogueDropdownState = (pageId: number | undefined) => {
  const pages = useSelector((state: StoreState) => state.page.dropdown);
  const selectedPage = pages.find(p => p.id === pageId);
  const pageGroups = useSelector(
    (state: StoreState) => state.pageGroup.entities,
  );

  const dialogueDropdownOptions = generatePageStructure(pages, pageGroups);

  return {
    dialogueDropdownOptions,
    selectedPage,
  };
};

const TAGS_TEXT = 'Add a Tag to contacts who click this button';

const getSelectedButton = (
  buttonsList: TextWidgetButtonDefinition[],
  selectedButtonType: ButtonType,
) => {
  return buttonsList.find(b => b.type === selectedButtonType) || buttonsList[0];
};

export const CreateEditButtonArea = (props: Props) => {
  const {
    button,
    buttonIndex,
    onCreateButtonHandler,
    onUpdateButtonHandler,
    closeButtonArea,
    setUnsavedEntryButton,
    widget,
    disabledButtonTypes,
    usedInSlideout,
  } = props;

  const pageCRUDActions = usePageCRUDActions();

  const chatbotId = useSelector(activeChatbotIdSelector);

  const { safeTimeout } = useSafeTimeout();
  const { inputRef, memoizedSetInputRef } = useInputRef(true);
  const { inputRef: textInputRef, memoizedSetInputRef: setTextInputRef } =
    useInputRef(true);

  const {
    pathGraph,
    setActiveNode,
    removeNodeAndSuccessors,
    addWidgetButtonsToGraph,
  } = useContext(PathGraphContext);

  const topLevelDialogue = useSelector((state: StoreState) =>
    pageEntityByIdSelector(state, pathGraph?.graph() || ''),
  );

  const buttonsList = [
    textWidgetButtons.dialogue,
    textWidgetButtons.phone,
    textWidgetButtons.url,
  ];

  const submitForm = ({
    values,
    buttonIndex,
    onCreateButtonHandler,
    onUpdateButtonHandler,
  }: {
    values: CreateEditButtonFormValues;
    buttonIndex: number;
    onCreateButtonHandler: (button: ButtonModel) => void;
    onUpdateButtonHandler: (buttonIndex: number, button: ButtonModel) => void;
  }) => {
    if (values) {
      const {
        buttonName,
        phoneNumber,
        url,
        webviewSize,
        appId,
        pageId,
        selectedButtonType,
        tags,
      } = values;
      const formValues = {
        name: buttonName,
        phone: phoneNumber,
        url,
        webviewSize,
        appId,
        page: pageId,
        selectedButtonType,
        tags,
      };
      const buttonFactory = MAP_ENTITY[values.selectedButtonType];
      const button = isFunction(buttonFactory)
        ? buttonFactory({ ...props, formValues })
        : null;
      const buttonIndexNumber = Number(buttonIndex);
      if (buttonIndex === -1) {
        onCreateButtonHandler(button);
      } else {
        onUpdateButtonHandler(buttonIndexNumber, button);
      }
    }
  };

  const formProps = useFormik<CreateEditButtonFormValues>({
    initialValues: FormHelpers.getInitialValues(button as ButtonModel),
    validate: FormHelpers.validate(true),
    onSubmit: values =>
      submitForm({
        values,
        buttonIndex,
        onCreateButtonHandler,
        onUpdateButtonHandler,
      }),
  });

  useEffect(() => {
    if (button) {
      formProps.setFieldValue(
        FormHelpers.formFields.selectedButtonType,
        button.type,
      );
    }
  }, [button]);

  const selectedButton = getSelectedButton(
    buttonsList,
    formProps.values.selectedButtonType,
  );

  const { dialogueDropdownOptions, selectedPage } = useDialogueDropdownState(
    Number(formProps.values.pageId),
  );

  useEffect(() => {
    if (selectedPage) {
      formProps.setFieldValue(FormHelpers.formFields.pageId, selectedPage.id);
    }
  }, []);

  const updateInsertEmojiQuestion = (emoji: string) => {
    if (inputRef) {
      const newText = getUpdatedTextWithEmoji(
        formProps.values.buttonName,
        emoji,
        inputRef,
      );
      formProps.setFieldValue(FormHelpers.formFields.buttonName, newText);
    }
  };

  const onAttributePicked = (attribute: string) => {
    const newText = getUpdatedTextWithAttribute(
      formProps.values.url,
      attribute,
      textInputRef,
    );

    formProps.setFieldValue(FormHelpers.formFields.url, newText);
    formProps.setFieldTouched(FormHelpers.formFields.url);

    safeTimeout(() => {
      if (textInputRef) {
        textInputRef.focus();
      }
    }, 100);
  };

  const onClickButtonType = (buttonType: ButtonType) => {
    formProps.setFieldValue(
      FormHelpers.formFields.selectedButtonType,
      buttonType,
    );
    formProps.validateForm();
  };

  const onTagAdd = (tag: { name: string }) => {
    formProps.setFieldValue(FormHelpers.formFields.tags, [
      ...formProps.values.tags,
      tag,
    ]);
  };

  const onTagRemove = (tag: { name: string }) => {
    formProps.setFieldValue(
      FormHelpers.formFields.tags,
      formProps.values.tags.filter(e => e.name !== tag.name),
    );
  };

  const [previousActiveButtonId, setPreviousActiveButtonId] = useState<
    undefined | number
  >(undefined);

  useEffect(() => {
    if (selectedPage) {
      const unsavedEntryButton = {
        ...button,
        name: formProps.values.buttonName,
        config: {
          ...button.config,
          pageId: selectedPage.id,
        },
      } as ButtonModel;

      if (
        button.id === undefined &&
        formProps.values.selectedButtonType === ButtonType.PAGE
      ) {
        addWidgetButtonsToGraph?.(widget.id, [
          { ...unsavedEntryButton, id: -1 },
        ]);
      }

      if (
        !previousActiveButtonId ||
        previousActiveButtonId !== selectedPage.id
      ) {
        if (formProps.values.selectedButtonType === ButtonType.PAGE) {
          if ((button.config as PageButtonConfig)?.pageId !== selectedPage.id) {
            setUnsavedEntryButton?.({
              ...unsavedEntryButton,
              id: button.id || -1,
            });
          }

          setActiveNode?.({
            id: button.id || -1,
            type: 'button',
            dialogueId: selectedPage.id,
            action: 'open',
          });
          setPreviousActiveButtonId(selectedPage.id);
        }
      }
    }

    return () => {
      if (formProps.values.selectedButtonType === ButtonType.PAGE) {
        setUnsavedEntryButton?.(undefined);
        if (selectedPage) {
          setPreviousActiveButtonId(undefined);
          if (button.id === undefined) {
            removeNodeAndSuccessors?.({
              id: -1,
              type: 'button',
              parentId: widget.id,
              parentType: 'widget',
              actionType: 'delete',
            });
          }
        }
      }
    };
  }, [selectedPage, formProps.values.selectedButtonType]);

  const getNewDialogueName = () => {
    return `${
      formProps.values.buttonName === 'buttonName' ||
      formProps.values.buttonName === ''
        ? moment().format('YYYY-MM-DD HH:mm')
        : formProps.values.buttonName
    }`;
  };

  const [showSearchRightSide, setShowSearchRightSide] = useState(true);
  const [showUnderSearchInsert, setShowUnderSearchInsert] = useState(false);
  const [newDialogueName, setNewDialogueName] = useState(getNewDialogueName());

  useEffect(() => {
    setNewDialogueName(getNewDialogueName());
  }, [formProps.values.buttonName]);

  const searchRightSide = useMemo(
    () => (
      <div
        className="create-edit-button-area__associated-dialogue-input--new-dialogue"
        onClick={() => {
          setShowUnderSearchInsert(true);
          setShowSearchRightSide(false);
        }}
      >
        {showSearchRightSide && '+ New Dialogue'}
      </div>
    ),
    [showSearchRightSide, setShowSearchRightSide, setShowUnderSearchInsert],
  );

  const createNewDialogue = async () => {
    setShowUnderSearchInsert(false);
    const response = await pageCRUDActions.create({
      botId: chatbotId,
      name: newDialogueName,
      kind: PageKind.StartBot,
      pageGroupId: topLevelDialogue?.pageGroupId,
    });

    if (response?.success) {
      const { page } = response;
      formProps.setFieldValue(FormHelpers.formFields.pageId, page.id);
    }
  };

  const underSearchInsert = useMemo(
    () =>
      showUnderSearchInsert ? (
        <div className="create-edit-button-area__new-dialogue-container">
          <TextInput
            className="create-edit-button-area__new-dialogue-input"
            value={newDialogueName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setNewDialogueName(e.target.value)
            }
            placeholder="Name New Dialogue"
            autoFocus
          />
          <PrimaryButton
            className="create-edit-button-area__new-dialogue-button"
            onClick={createNewDialogue}
            size="lg"
          >
            Create
          </PrimaryButton>
        </div>
      ) : undefined,
    [
      showUnderSearchInsert,
      newDialogueName,
      setNewDialogueName,
      createNewDialogue,
    ],
  );

  const isSaveDisabled =
    !formProps.isValid ||
    (formProps.values.selectedButtonType === ButtonType.PAGE && !selectedPage);

  const userCountry = useSelector((store: StoreState) => store.env.userCountry);

  return (
    <form onSubmit={formProps.handleSubmit} className="create-edit-button-area">
      <div className="create-edit-button-area__text-wrapper">
        <WrappedTextInput
          fieldName={FormHelpers.formFields.buttonName}
          formProps={formProps}
          maxLength={MAX_BUTTON_LENGTH}
          inputRef={memoizedSetInputRef}
          className="create-edit-button-area__name-input"
          placeholder="Button name"
        />

        <EmojiPicker
          onEmojiPicked={updateInsertEmojiQuestion}
          className={classnames('create-edit-button-area__emoji-picker', {
            'create-edit-button-area--emoji-picker-disabled':
              formProps.values.buttonName.length >= MAX_BUTTON_LENGTH,
          })}
        />
      </div>

      <div className="create-edit-button-area__button-type-options">
        {buttonsList.map(
          ({ type, iconClassList, text, iconComponent: IconComponent }) => {
            const isSelected = selectedButton.type === type;
            const isDisabled = disabledButtonTypes?.includes(type);

            if (isDisabled && !isSelected) {
              return <></>;
            }

            return (
              <div
                key={type}
                onClick={() => onClickButtonType(type)}
                className={classnames('create-edit-button-area__button-type', {
                  'create-edit-button-area__button-type--selected': isSelected,
                  'create-edit-button-area__button-type--disabled': isDisabled,
                })}
              >
                {iconClassList && (
                  <i
                    className={classnames(
                      'create-edit-button-area__button-icon',
                      iconClassList,
                    )}
                  />
                )}

                {IconComponent && (
                  <IconComponent className="create-edit-button-area__button-icon create-edit-button-area__button-icon--component" />
                )}
                <span>{text}</span>
              </div>
            );
          },
        )}
      </div>

      <div className="create-edit-button-area__custom-fields-by-type">
        {disabledButtonTypes?.includes(selectedButton.type) && (
          <div className="create-edit-button-area__unsupported-button">
            <span className="create-edit-button-area__unsupported-button-platforms">
              Valid for:
              <WidgetPlatforms platforms={widget.supportedByPlatforms} />
            </span>

            <Alert type="warning" showCloseIcon={false}>
              Button will not work for current platform
            </Alert>
          </div>
        )}

        {selectedButton.type === ButtonType.CALL && (
          <FormFieldWrapper
            className="create-edit-button-area__url-fields-wrapper"
            error={formProps.errors.phoneNumber}
            touched={formProps.touched.phoneNumber}
          >
            <PhoneInput
              name={FormHelpers.formFields.phoneNumber}
              country={userCountry}
              className="create-edit-button-area__phone-input"
              value={formProps.values.phoneNumber}
              onChange={value =>
                formProps.setFieldValue(
                  FormHelpers.formFields.phoneNumber,
                  value,
                )
              }
              onBlur={() =>
                formProps.setFieldTouched(FormHelpers.formFields.phoneNumber)
              }
              errorState={
                formProps.touched.phoneNumber && !!formProps.errors.phoneNumber
              }
            />
          </FormFieldWrapper>
        )}

        {selectedButton.type === ButtonType.URL && (
          <>
            <div className="create-edit-button-area__field">
              <div className="create-edit-button-area__field-label">
                Website Address
              </div>
              <FormFieldWrapper
                className="create-edit-button-area__url-fields-wrapper"
                error={formProps.errors.url}
                touched={formProps.touched.url}
              >
                <AttributeContainingTextInput
                  inputRef={setTextInputRef}
                  value={formProps.values.url}
                  onChange={e =>
                    formProps.setFieldValue(
                      FormHelpers.formFields.url,
                      e.target.value,
                    )
                  }
                  onBlur={() =>
                    formProps.setFieldTouched(FormHelpers.formFields.url)
                  }
                  useStoreAttributes
                />
                <AttributePicker
                  className="button-config__input-url-field-attribute-picker"
                  onAttributePicked={onAttributePicked}
                />
              </FormFieldWrapper>
            </div>
            <div className="create-edit-button-area__field">
              <div className="create-edit-button-area__field-label">
                <div className="create-edit-button-area__field-label-text">
                  Webview Size
                </div>
                <TooltipContainer
                  classNameContent="create-edit-button-area__webview-size-tooltip"
                  classNameAnchor="create-edit-button-area__webview-size-anchor"
                  message={
                    <a
                      href="https://customers.ai/help/article/ibx5q9zk7i-webview-size"
                      target="_blank"
                      className="create-edit-button-area__webview-size-link"
                    >
                      About Webview Size
                    </a>
                  }
                />
              </div>
              <PrimarySelect
                showSearchFilter={false}
                placeholder="Select Webview Size"
                className="create-edit-button-area__select-webview"
                onChange={webviewSize =>
                  formProps.setFieldValue(
                    FormHelpers.formFields.webviewSize,
                    webviewSize.id,
                  )
                }
                options={WebviewOptions}
                value={WebviewOptions.find(
                  ({ id }) => id === formProps.values.webviewSize,
                )}
                customDropdownStyles={{
                  zIndex: usedInSlideout ? '1000003' : '999',
                }}
              />
            </div>
          </>
        )}

        {selectedButton.type === ButtonType.PAGE && (
          <div className="question-button-active-tab-area__associated-dialogue">
            <div className="question-button-active-tab-area__associated-dialogue-text">
              Take users to a new Dialogue?
            </div>

            <div className="question-button-active-tab-area__associated-dialogue-input-wrapper">
              <PrimarySelect
                showSearchFilter={true}
                placeholder="Select Dialogue"
                className="question-button-active-tab-area__associated-dialogue-input"
                onChange={dialogue =>
                  formProps.setFieldValue(
                    FormHelpers.formFields.pageId,
                    dialogue.id,
                  )
                }
                organizedOptions={dialogueDropdownOptions}
                value={selectedPage}
                searchRightSide={searchRightSide}
                underSearchInsert={underSearchInsert}
                customDropdownStyles={{
                  zIndex: usedInSlideout ? '1000003' : '999',
                }}
              />
            </div>
          </div>
        )}

        {[ButtonType.PAGE, ButtonType.URL].includes(selectedButton.type) && (
          <div className="create-edit-button-area__tags-wrapper">
            <ButtonTagsForm
              title={TAGS_TEXT}
              kind="add_tags"
              tags={formProps.values.tags}
              onTagAdd={onTagAdd}
              onTagRemove={onTagRemove}
            />
          </div>
        )}
      </div>

      <div className="create-edit-button-area__bottom-buttons">
        <PrimaryButton
          onClick={closeButtonArea}
          color={ButtonColor.secondaryBlue}
        >
          Cancel
        </PrimaryButton>

        <PrimaryButton
          disabled={isSaveDisabled}
          className="create-edit-button-area__save-button"
          onClick={formProps.submitForm}
        >
          {selectedButton ? 'Save' : 'Create'}
        </PrimaryButton>
      </div>
    </form>
  );
};
